import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../utils/queryKeys';
import { supabase } from '../utils/supabaseClient';

const fetchGroupCards = async (startIndex, count) => {
  const { data, error } = await supabase
    .from("cards")
    .select(`
      id,
      companyname,
      card_color,
      vid_brd_color,
      videoclosed,
      videoopen,
      companyoverviewid
    `)
    .range(startIndex, startIndex + count - 1);

  if (error) throw error;
  return data;
};

export const useCardGroupPrefetch = ({ groupSize = 5, maxConcurrentRequests = 2 }) => {
  const queryClient = useQueryClient();
  const activeRequests = useRef(0);
  const [isPrefetching, setIsPrefetching] = useState(false);

  const prefetchCardGroup = useCallback(async (groupIndex, totalGroups) => {
    // Validar límites del grupo
    if (groupIndex < 0 || groupIndex >= totalGroups) return;
    
    // Controlar número de peticiones concurrentes
    if (activeRequests.current >= maxConcurrentRequests) return;

    try {
      setIsPrefetching(true);
      activeRequests.current += 1;

      const startIndex = groupIndex * groupSize;
      
      // Prefetch de cards del grupo
      const cardsData = await fetchGroupCards(startIndex, groupSize);
      
      // Prefetch de cada card y sus recursos
      await Promise.all(cardsData.map(async (card) => {
        // Prefetch datos de la card
        await queryClient.prefetchQuery({
          queryKey: queryKeys.cards.byId(card.id),
          queryFn: async () => card,
          staleTime: 1000 * 60 * 5 // 5 minutos
        });

        // Prefetch datos de la compañía
        if (card.companyoverviewid) {
          await queryClient.prefetchQuery({
            queryKey: queryKeys.company.byId(card.companyoverviewid),
            queryFn: async () => {
              const { data, error } = await supabase
                .from("company_overview")
                .select("*")
                .eq("id", card.companyoverviewid)
                .single();
              
              if (error) throw error;
              return data;
            },
            staleTime: 1000 * 60 * 5
          });
        }

        // Prefetch de videos
        if (card.videoclosed) {
          const link = document.createElement('link');
          link.rel = 'preload';
          link.as = 'video';
          link.href = card.videoclosed;
          document.head.appendChild(link);
        }
      }));

    } catch (error) {
      console.warn('Prefetch warning:', error);
    } finally {
      activeRequests.current -= 1;
      setIsPrefetching(false);
    }
  }, [queryClient, groupSize, maxConcurrentRequests]);

  const handleGroupVisibility = useCallback((groupIndex, totalGroups) => {
    // Prefetch del grupo actual si no está en caché
    prefetchCardGroup(groupIndex, totalGroups);
    
    // Prefetch del grupo siguiente
    prefetchCardGroup(groupIndex + 1, totalGroups);
    
    // Si no es el primer grupo, prefetch del grupo anterior
    if (groupIndex > 0) {
      prefetchCardGroup(groupIndex - 1, totalGroups);
    }
  }, [prefetchCardGroup]);

  return {
    handleGroupVisibility,
    isPrefetching,
    prefetchCardGroup
  };
};